import { motion } from "framer-motion";
import { MapPin, Clock, Phone, Car, Navigation } from "lucide-react";
import { BUSINESS } from "../data/content";
import { SectionLabel } from "./Section";
import { fadeUp, stagger, viewport } from "../lib/motion";

const VisitUs = () => (
  <section id="visit" className="relative py-16 sm:py-32" data-testid="visit-section">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-stretch">
        {/* Info */}
        <div>
          <SectionLabel>Visit Us</SectionLabel>
          <motion.h2
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={viewport}
            className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.1]"
          >
            Find us in the heart of <span className="text-highlight">Arlington.</span>
          </motion.h2>

          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={viewport}
            className="mt-10 space-y-7"
          >
            <motion.div variants={fadeUp} className="flex gap-4" data-testid="visit-address">
              <div className="h-11 w-11 shrink-0 rounded-2xl bg-surface flex items-center justify-center text-primary">
                <MapPin size={20} strokeWidth={1.6} />
              </div>
              <div>
                <p className="text-xs uppercase tracking-label text-foreground/50 font-semibold">Address</p>
                <p className="mt-1 text-foreground">{BUSINESS.address}</p>
                <p className="text-sm text-foreground/55">{BUSINESS.neighborhood}</p>
              </div>
            </motion.div>

            <motion.div variants={fadeUp} className="flex gap-4" data-testid="visit-hours">
              <div className="h-11 w-11 shrink-0 rounded-2xl bg-surface flex items-center justify-center text-primary">
                <Clock size={20} strokeWidth={1.6} />
              </div>
              <div>
                <p className="text-xs uppercase tracking-label text-foreground/50 font-semibold">Hours</p>
                <ul className="mt-1 space-y-0.5">
                  {BUSINESS.hours.map((h) => (
                    <li key={h.day} className="flex justify-between gap-8 text-foreground">
                      <span>{h.day}</span>
                      <span className="text-foreground/70">{h.time}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>

            <div className="grid sm:grid-cols-2 gap-7">
              <motion.div variants={fadeUp} className="flex gap-4" data-testid="visit-phone">
                <div className="h-11 w-11 shrink-0 rounded-2xl bg-surface flex items-center justify-center text-primary">
                  <Phone size={20} strokeWidth={1.6} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-label text-foreground/50 font-semibold">Phone</p>
                  <a href={BUSINESS.phoneHref} className="mt-1 block text-foreground hover:text-primary transition-colors">{BUSINESS.phone}</a>
                </div>
              </motion.div>

              <motion.div variants={fadeUp} className="flex gap-4" data-testid="visit-parking">
                <div className="h-11 w-11 shrink-0 rounded-2xl bg-surface flex items-center justify-center text-primary">
                  <Car size={20} strokeWidth={1.6} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-label text-foreground/50 font-semibold">Parking</p>
                  <p className="mt-1 text-foreground">Free street parking</p>
                  <p className="text-sm text-foreground/55">Steps from Metro</p>
                </div>
              </motion.div>
            </div>

            <motion.a
              variants={fadeUp}
              href={BUSINESS.directions}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="directions-btn"
              className="group inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-8 py-4 text-sm font-semibold tracking-wide transition-transform duration-300 hover:scale-[1.03] active:scale-[0.98]"
            >
              <Navigation size={17} strokeWidth={1.8} />
              Get Directions
            </motion.a>
          </motion.div>
        </div>

        {/* Map */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={viewport}
          className="relative min-h-[380px] lg:min-h-full overflow-hidden rounded-3xl shadow-warm-lg border border-surface"
          data-testid="map-embed"
        >
          <iframe
            title="Detour Coffee Co. location map"
            src={BUSINESS.mapEmbed}
            className="absolute inset-0 h-full w-full"
            style={{ border: 0, filter: "grayscale(0.2) contrast(1.05)" }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
          />
        </motion.div>
      </div>
    </div>
  </section>
);

export default VisitUs;
